import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import supabase from './lib/supabase';

// Components
import AuthPage from './components/AuthPage';
import PasswordResetPage from './components/PasswordResetPage';
import Dashboard from './components/Dashboard';
import QuestionForm from './components/QuestionForm';
import LearningForm from './components/LearningForm';
import FeedbackForm from './components/FeedbackForm';
import QuestionsPage from './components/QuestionsPage';
import QuestionDetails from './components/QuestionDetails';
import LearningsPage from './components/LearningsPage';
import LearningDetails from './components/LearningDetails';
import FeedbackPage from './components/FeedbackPage';
import KnowledgeBase from './components/KnowledgeBase';
import Profile from './components/Profile';
import ModulePage from './components/ModulePage';
import QuickStartGuide from './components/QuickStartGuide';
import './App.css';

const { FiLoader } = FiIcons;

function App() {
  const [currentView, setCurrentView] = useState('auth');
  const [selectedItem, setSelectedItem] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already signed in
    const checkAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;

        if (session?.user) {
          setUser(session.user);
          setCurrentView('dashboard');
        } else {
          setCurrentView('auth');
        }
      } catch (error) {
        console.error('Error checking auth:', error);
        setCurrentView('auth');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          setUser(session.user);
          setCurrentView('dashboard');
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setCurrentView('auth');
        } else if (event === 'PASSWORD_RECOVERY') {
          setCurrentView('password-reset');
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const navigate = (view, item = null) => {
    setCurrentView(view);
    setSelectedItem(item);
  };

  const handleAuthSuccess = (user) => {
    setUser(user);
    setCurrentView('dashboard');
  };

  const handleSignOut = () => {
    setUser(null);
    setCurrentView('auth');
  };

  const handlePasswordResetComplete = () => {
    setCurrentView('dashboard');
  };

  const renderView = () => {
    if (!user && currentView !== 'auth' && currentView !== 'password-reset') {
      return <AuthPage onAuthSuccess={handleAuthSuccess} />;
    }

    switch (currentView) {
      case 'auth':
        return <AuthPage onAuthSuccess={handleAuthSuccess} />;
      case 'password-reset':
        return <PasswordResetPage onResetComplete={handlePasswordResetComplete} />;
      case 'quick-start-guide':
        return <QuickStartGuide onNavigate={navigate} />;
      case 'question-form':
        return (
          <QuestionForm
            onBack={() => navigate('dashboard')}
            preselectedCategory={selectedItem}
          />
        );
      case 'learning-form':
        return (
          <LearningForm
            onBack={() => navigate('dashboard')}
            preselectedCategory={selectedItem}
          />
        );
      case 'feedback-form':
        return (
          <FeedbackForm
            onBack={() => navigate('feedback')}
            preselectedCategory={selectedItem}
          />
        );
      case 'questions':
        return <QuestionsPage onNavigate={navigate} />;
      case 'questions-filtered':
        return <QuestionsPage onNavigate={navigate} initialFilter={selectedItem} />;
      case 'question-details':
        return <QuestionDetails questionId={selectedItem} onNavigate={navigate} />;
      case 'learnings':
        return <LearningsPage onNavigate={navigate} />;
      case 'learning-details':
        return <LearningDetails learningId={selectedItem} onNavigate={navigate} />;
      case 'feedback':
        return <FeedbackPage onNavigate={navigate} />;
      case 'knowledge-base':
        return <KnowledgeBase onNavigate={navigate} />;
      case 'profile':
        return <Profile onNavigate={navigate} user={user} />;
      case 'module-certification':
        return <ModulePage category="Certification" onNavigate={navigate} />;
      case 'module-qdms':
        return <ModulePage category="QDMS" onNavigate={navigate} />;
      case 'module-qhse':
        return <ModulePage category="QHSE" onNavigate={navigate} />;
      case 'module-forms':
        return <ModulePage category="Forms and Checklists" onNavigate={navigate} />;
      case 'module-procurement':
        return <ModulePage category="Procurement" onNavigate={navigate} />;
      case 'module-maintenance':
        return <ModulePage category="Maintenance" onNavigate={navigate} />;
      case 'module-general':
        return <ModulePage category="General" onNavigate={navigate} />;
      default:
        return (
          <Dashboard
            onNavigate={navigate}
            user={user}
            onSignOut={handleSignOut}
          />
        );
    }
  };

  // Loading screen
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiLoader} className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">PAL-NG Super User Hub</h2>
          <p className="text-gray-600">Loading...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentView}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {renderView()}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export default App;